import { useState, type ReactElement } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiClientError } from '../api/client';
import { getDashboard } from '../api/operations';
import { BookingDetailDialogById } from '../components/BookingDetailDialog';
import { useAuth } from '../auth/AuthProvider';
import { Icon, type IconName } from '../lib/icons';
import { dateLabel, timeLabel } from '../lib/calendar';

/** Each KPI's icon and the screen it summarises. */
const KPI_META: Record<string, { icon: IconName; path: string }> = {
  open_tickets: { icon: 'chat', path: '/support' },
  visitors_onsite: { icon: 'visitor', path: '/visitors' },
  bookings_today: { icon: 'calendar', path: '/bookings' },
  active_employees: { icon: 'people', path: '/employees' },
};

const QUICK_ACTION_META: Record<string, { icon: IconName; path: string }> = {
  new_ticket: { icon: 'chat', path: '/support' },
  register_visitor: { icon: 'visitor', path: '/visitors' },
  add_employee: { icon: 'people', path: '/employees' },
};

/**
 * FEAT-003 — the dashboard.
 *
 * Every card comes from the server's one aggregation call. Which cards exist is
 * the server's answer too: a card the caller may not see is simply absent from
 * the response (BR-008), so there is nothing here that decides what to hide.
 */
export function DashboardPage(): ReactElement {
  /** Which upcoming meeting is open, if any. */
  const [openedId, setOpenedId] = useState<string | null>(null);

  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['dashboard'], queryFn: getDashboard });

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">
            {t('welcome')}, {user?.name}
          </div>
          <h1>{t('dashboard')}</h1>
          <div className="lead">{t('overview')}</div>
        </div>
      </div>

      {query.isPending ? (
        <div className="empty" role="status" aria-live="polite">
          {t('loading')}
        </div>
      ) : query.error ? (
        <div className="empty" role="alert">
          {query.error instanceof ApiClientError ? query.error.message : t('login_network_error')}
        </div>
      ) : (
        <>
          <div className="kpis">
            {query.data?.kpis.map((kpi) => {
              const meta = KPI_META[kpi.key];
              return (
                <button
                  key={kpi.key}
                  type="button"
                  className="kpi"
                  onClick={() => meta && navigate(meta.path)}
                >
                  <div className="kl">
                    {meta ? <Icon name={meta.icon} /> : null}
                    {t(`kpi_${kpi.key}`)}
                  </div>
                  <div className="kv num">{kpi.value}</div>
                </button>
              );
            })}

            {/*
              A card whose source failed shows "unavailable" rather than a zero.
              Drawing 0 would be a claim about the data that we cannot make.
            */}
            {query.data?.degraded.map((key) => (
              <div className="kpi" key={key}>
                <div className="kl">
                  {KPI_META[key] ? <Icon name={KPI_META[key].icon} /> : null}
                  {t(`kpi_${key}`)}
                </div>
                <div className="kv">{t('kpi_unavailable')}</div>
              </div>
            ))}
          </div>

          {/*
            The one card here about the reader rather than about the building.
            Every other number is a count of somebody else's work; this answers
            "what do I have next", which is what somebody opening a dashboard in
            the morning is actually asking.

            Above the two-column row deliberately — it is the most personal thing
            on the screen and the most likely to be acted on.
          */}
          <div className="card">
            <div className="card-head">
              <h2>{t('dash_upcoming')}</h2>
              <button type="button" className="btn sm" onClick={() => navigate('/bookings')}>
                {t('dash_open_calendar')}
              </button>
            </div>

            <div style={{ padding: '8px 18px 14px' }}>
              {(query.data?.upcoming.length ?? 0) === 0 ? (
                <div className="empty">{t('dash_no_upcoming')}</div>
              ) : (
                <ul className="tl">
                  {query.data?.upcoming.map((meeting) => (
                    <li key={meeting.id}>
                      <span className="tdot" />
                      {/*
                        A real button, not a clickable row. It is the only way
                        the keyboard reaches this, and it is what makes a screen
                        reader announce the meeting as something that can be
                        opened rather than as a paragraph of text.
                      */}
                      <button
                        type="button"
                        className="tl-open"
                        onClick={() => setOpenedId(meeting.id)}
                      >
                        <div className="tx">{meeting.title}</div>
                        {/*
                          Time, date and room on one line, in that order — the
                          three facts somebody needs to decide whether to stand
                          up. `<bdi>` on the room, which may be a Latin name in
                          an Arabic page.
                        */}
                        <div className="tm">
                          {timeLabel(meeting.starts_at, i18n.language)}–
                          {timeLabel(meeting.ends_at, i18n.language)} ·{' '}
                          {dateLabel(meeting.date, i18n.language)} · <bdi>{meeting.room_name}</bdi>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className={query.data?.recent_activity ? 'two-col' : ''}>
            {/* Null, not empty: the feed is absent for roles without the capability. */}
            {query.data?.recent_activity ? (
              <div className="card">
                <div className="card-head">
                  <h2>{t('recent')}</h2>
                </div>
                <div style={{ padding: '8px 18px 14px' }}>
                  {query.data.recent_activity.length === 0 ? (
                    <div className="empty">{t('none_yet')}</div>
                  ) : (
                    <ul className="tl">
                      {query.data.recent_activity.map((entry) => (
                        <li key={entry.id}>
                          <span className="tdot" />
                          <div>
                            <div className="tx">{entry.event}</div>
                            <div className="tm">
                              {timeLabel(entry.at, i18n.language)} · {entry.actor}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ) : null}

            <div className="card">
              <div className="card-head">
                <h2>{t('quick')}</h2>
              </div>
              <div
                style={{
                  padding: '14px 18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 9,
                }}
              >
                {query.data && query.data.quick_actions.length > 0 ? (
                  query.data.quick_actions.map((action) => {
                    const meta = QUICK_ACTION_META[action];
                    return (
                      <button
                        key={action}
                        type="button"
                        className="btn qa-btn"
                        style={{ justifyContent: 'flex-start' }}
                        onClick={() => meta && navigate(meta.path)}
                      >
                        {meta ? <Icon name={meta.icon} /> : null}
                        {t(`qa_${action}`)}
                      </button>
                    );
                  })
                ) : (
                  <div className="empty" style={{ padding: '16px 0' }}>
                    {t('none_yet')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/*
        The same dialog the Room Booking page opens, not a second rendering of
        the same facts — the client's requirement is that the two look exactly
        alike, and the copy that used to live here had already drifted.
      */}
      {openedId ? (
        <BookingDetailDialogById
          id={openedId}
          onClose={() => setOpenedId(null)}
          onChanged={() => {
            setOpenedId(null);
            void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
          }}
        />
      ) : null}
    </>
  );
}
